import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Barcelona (barcelona package) — the one destination outside Taiwan, reached by plane from Taoyuan.
 *
 * North: Park Güell on its hill (west) and the Eixample grid with the Sagrada Família (east), joined by
 * a wide avenue. Middle: La Rambla running from Plaça Catalunya down to the Columbus column, with the
 * Boqueria market on its west side; the Barri Gòtic (cathedral, Plaça Reial, Carrer del Bisbe, Plaça
 * Sant Jaume) east of it, and the Ciutadella park with the Arc de Triomf further east. South: Port Vell,
 * the Barceloneta beach up to the W hotel, and El Prat's arrivals hall in the south-west corner.
 *
 * Ground chars of this package: '<' panot pavement (terminal floor inside AIRPORT, stone steps inside
 * STAIRS), '>' cobbles (gravel in Park Güell, the wavy Rambla paving in RAMBLA, sand in BEACH, the Miró
 * mosaic in MIRO), '|' solid building blocks (their facades are `bc_facade` decor).
 */
const W = 96, H = 56;
const b = new MapBuilder(W, H, 'g');

// ============================================================================================
// NORTH: Park Güell (x 0..31) · the Eixample and the Sagrada Família (x 32..95) · the avenue
// ============================================================================================
// --- Park Güell: a wooded hill, the gatehouses, the dragon stairway, the Hypostyle hall, the terrace
b.fill(0, 0, 32, 21, 'g');
b.fill(0, 20, 32, 1, 'h'); // park wall along the avenue
b.fill(15, 20, 2, 1, '>').fill(26, 20, 2, 1, '>'); // gate · east entrance
b.fill(13, 15, 6, 5, '>'); // forecourt between the gatehouses
b.fill(14, 11, 4, 4, '<'); // the dragon stairway (STAIRS → steps)
b.fill(10, 8, 12, 3, '>'); // Hypostyle hall (columns are decor)
b.fill(7, 2, 18, 6, '>'); // Plaça de la Natura (the terrace with the serpentine bench)
b.fill(3, 3, 3, 16, '>').fill(3, 18, 11, 2, '>'); // west path up to the lookout
b.fill(24, 5, 3, 15, '>').fill(22, 18, 5, 2, '>'); // east path to the Lesseps entrance
b.fill(1, 1, 30, 1, 'g');
b.fill(0, 0, 32, 1, 'h'); // hilltop hedge line
// --- The Eixample: two blocks west of the Sagrada, two east, streets between -------------------
b.fill(32, 0, 64, 21, '<');
b.fill(48, 0, 2, 21, 'r').fill(76, 0, 2, 21, 'r'); // Carrer de Sardenya · Carrer de Marina
b.fill(32, 9, 16, 2, 'r').fill(78, 9, 18, 2, 'r'); // cross streets
b.fill(48, 9, 2, 2, '=').fill(76, 9, 2, 2, '=');
b.fill(33, 1, 13, 7, '|').fill(33, 12, 13, 8, '|'); // west blocks
b.fill(80, 1, 14, 7, '|').fill(80, 12, 14, 8, '|'); // east blocks
// --- The Sagrada Família and its square, with the pond that reflects the Nativity facade ---------
b.fill(54, 2, 18, 9, '|');
b.fill(51, 0, 24, 2, 'g');
b.fill(51, 2, 3, 9, 'g').fill(72, 2, 3, 9, 'g');
b.fill(58, 15, 10, 2, '~');
// --- The avenue (Gran Via): pavement · 3 lanes · pavement ------------------------------------------
b.fill(0, 21, 96, 1, '<');
b.fill(0, 22, 96, 1, 'r').fill(0, 23, 96, 1, 'l').fill(0, 24, 96, 1, 'r');
b.fill(0, 25, 96, 1, '<');
for (const tx of [15, 26, 30, 48, 62, 76, 84]) b.fill(tx, 22, 2, 3, '=');

// ============================================================================================
// MIDDLE: La Rambla (x 16..41) · Barri Gòtic (x 42..72) · Parc de la Ciutadella (x 73..95)
// ============================================================================================
b.fill(0, 26, 96, 18, '<');
// --- Plaça Catalunya and La Rambla: promenade between two one-way streets --------------------------
b.fill(28, 29, 6, 13, '>'); // the promenade (RAMBLA → wavy paving)
b.fill(26, 29, 2, 15, 'r').fill(34, 29, 2, 15, 'r');
b.fill(26, 42, 2, 2, '=').fill(34, 42, 2, 2, '=');
b.fill(16, 29, 8, 9, '|'); // west facades (the Boqueria is among them)
b.fill(38, 29, 4, 5, '|').fill(38, 39, 4, 5, '|'); // east facades, with Plaça Reial opening between
// --- Drassanes and the airport access road --------------------------------------------------------
b.fill(0, 40, 26, 2, 'r');
b.fill(16, 40, 2, 2, '=').fill(24, 40, 2, 2, '=');
// --- Barri Gòtic: the cathedral, blocks cut by alleys, the squares ----------------------------------
b.fill(42, 26, 31, 7, '|');
b.fill(50, 26, 10, 7, '|'); // cathedral footprint (same char; the facade is bc_cathedral decor)
b.fill(64, 26, 2, 7, '>'); // alley north to the avenue
b.fill(38, 33, 35, 6, '>'); // Plaça Reial (west) · Pla de la Seu (east)
b.fill(42, 39, 31, 5, '|');
b.fill(50, 39, 2, 5, '>'); // Carrer del Bisbe (under the bridge) down to the port
b.fill(64, 36, 9, 6, '>'); // Plaça Sant Jaume
b.fill(64, 39, 9, 3, '>');
// --- Parc de la Ciutadella: lawns, promenade under the Arc de Triomf, the pond, the petanca court ----
b.fill(73, 26, 23, 18, 'g');
b.fill(73, 26, 1, 10, 'h').fill(73, 42, 1, 2, 'h');
b.fill(82, 26, 4, 18, 'p'); // Passeig de Lluís Companys
b.fill(74, 36, 22, 2, 'p');
b.fill(74, 30, 6, 4, '~'); // boating pond
b.fill(74, 39, 7, 3, 'p'); // petanca court (gravel)
b.fill(88, 28, 6, 6, 'G'); // flower beds by the Cascada
b.fill(86, 40, 9, 2, 'p');

// ============================================================================================
// SOUTH: El Prat arrivals (x 0..15) · Port Vell (x 16..41) · Barceloneta (x 42..95)
// ============================================================================================
b.fill(0, 44, 96, 2, '<'); // Moll de la Fusta and the beach promenade
b.fill(16, 46, 26, 10, 'Z'); // Port Vell
b.fill(30, 46, 2, 6, 'y').fill(28, 51, 6, 2, 'y'); // Rambla de Mar walkway and its deck
b.fill(42, 46, 54, 5, '>'); // the sand (BEACH)
b.fill(42, 51, 54, 5, 'Z'); // the Mediterranean
b.fill(90, 44, 6, 6, '|'); // W hotel footprint on the breakwater
// --- The airport corner: forecourt, Aerobús and taxi rank, terminal ------------------------------
b.fill(0, 38, 16, 2, '<').fill(0, 42, 16, 8, '<');
b.fill(15, 46, 1, 4, 'f'); // railing between the forecourt and the water
b.fill(0, 50, 16, 6, '|');

// ============================================================================================
// Decor
// ============================================================================================
const mid = (tx: number, ty: number, dx = 8, dy = 14) => ({ x: px(tx) + dx, y: px(ty) + dy });
const solidAt = (tx: number, ty: number, tw: number, h = 8, inset = 1) => ({ x: px(tx) + inset, y: px(ty + 1) - h, w: px(tw) - inset * 2, h });
/** Wide decor is anchored at its centre (the prop culling assumes it). */
const span = (tx: number, tw: number) => ({ x: px(tx) + px(tw) / 2, w: px(tw) });
const tree = (tx: number, ty: number, kind: 'tree' | 'pine' | 'cherry' = 'tree'): Decor => ({ kind, ...mid(tx, ty), solid: { x: px(tx) + 3, y: px(ty) + 9, w: 10, h: 5 } });
const palm = (tx: number, ty: number, v = 0, dx = 8): Decor => ({ kind: 'hc_palm', x: px(tx) + dx, y: px(ty) + 14, w: 56, variant: v, solid: { x: px(tx) + dx - 3, y: px(ty) + 10, w: 6, h: 4 } });
const plane = (tx: number, ty: number, v = 0, dx = 8): Decor => ({ kind: 'bc_plane', x: px(tx) + dx, y: px(ty) + 14, w: 52, variant: v, solid: { x: px(tx) + dx - 3, y: px(ty) + 10, w: 6, h: 4 } });
const person = (tx: number, ty: number, v: number, dx = 8, dy = 12): Decor => ({ kind: 'xm_person', x: px(tx) + dx, y: px(ty) + dy, variant: v });
const crowd = (tx: number, ty: number, n: number, w: number, h: number, mode = 0, seed = 0): Decor => ({ kind: 'bc_crowd', x: px(tx), y: px(ty), w, h, variant: n + 16 * seed + 256 * mode });
const benchSit = (tx: number, ty: number, seed = 0): Decor => ({ kind: 'bc_benchSit', x: px(tx), y: px(ty), w: 30, variant: seed, solid: { x: px(tx) - 15, y: px(ty) - 10, w: 30, h: 10 } });
const pigeons = (tx: number, ty: number, v: number, w = 30): Decor => ({ kind: 'bc_pigeons2', x: px(tx), y: px(ty), w, variant: v });
/** A block facade `tw` tiles wide and `th` tall; `variant` 0-3 Eixample (ochre, cream, rose, grey), 4-5 Gòtic stone, 6 Barceloneta. */
const facade = (tx: number, ty: number, tw: number, th: number, variant: number): Decor => ({ kind: 'bc_facade', ...span(tx, tw), y: px(ty + th), h: px(th), variant });
const lamp = (tx: number, ty: number, dx = 8): Decor => ({ kind: 'bc_lamp', x: px(tx) + dx, y: px(ty) + 14, solid: { x: px(tx) + dx - 2, y: px(ty) + 11, w: 4, h: 3 } });
const falques = (tx: number, ty: number): Decor => ({ kind: 'bc_falques', x: px(tx) + 8, y: px(ty) + 14, w: 40, solid: { x: px(tx) - 8, y: px(ty) + 8, w: 32, h: 6 } });
const bicing = (tx: number, ty: number, tw: number): Decor => ({ kind: 'bc_bicing', ...span(tx, tw), y: px(ty) + 14, solid: solidAt(tx, ty, tw, 7) });
const taxi = (tx: number, ty: number, v = 0): Decor => ({ kind: 'bc_taxi', x: px(tx) + 8, y: px(ty) + 12, w: 34, variant: v, solid: { x: px(tx) - 6, y: px(ty) + 2, w: 28, h: 10 } });
const terrace = (tx: number, ty: number, v = 0): Decor => ({ kind: 'bc_terrace', x: px(tx) + 8, y: px(ty) + 14, w: 44, variant: v, solid: { x: px(tx) - 10, y: px(ty) + 4, w: 36, h: 10 } });

const decor: Decor[] = [
  // ---------------------------------------------------------------------------------------------
  // Park Güell
  // ---------------------------------------------------------------------------------------------
  { kind: 'bc_gatehouse', x: px(11), y: px(19) + 12, w: 52, solid: { x: px(9) + 2, y: px(16) + 8, w: 60, h: 48 } },
  { kind: 'bc_gatehouse', x: px(21), y: px(19) + 12, w: 52, variant: 1, solid: { x: px(19) + 2, y: px(16) + 8, w: 60, h: 48 } },
  { kind: 'bc_guellGate', x: px(16), y: px(20) + 14, w: 36 },
  { kind: 'bc_guellWall', ...span(0, 9), y: px(20) + 12 },
  { kind: 'bc_guellWall', ...span(23, 3), y: px(20) + 12 },
  { kind: 'bc_guellWall', ...span(28, 4), y: px(20) + 12 },
  { kind: 'bc_columns', ...span(10, 12), y: px(8) + 2 }, // hall columns: the back row (behind the player)
  { kind: 'bc_columns', ...span(10, 12), y: px(10) + 14, variant: 1, solid: { x: px(10), y: px(10) + 9, w: 16, h: 5 } },
  { kind: 'bc_solid', x: px(21), y: px(10) + 14, solid: { x: px(21), y: px(10) + 9, w: 16, h: 5 } },
  { kind: 'bc_terraceEdge', ...span(7, 18), y: px(8) }, // the terrace parapet over the hall (gap at the stairs)
  { kind: 'bc_solid', x: px(10), y: px(8), solid: { x: px(7), y: px(7) + 10, w: px(7) - 2, h: 6 } },
  { kind: 'bc_solid', x: px(21), y: px(8), solid: { x: px(18) + 2, y: px(7) + 10, w: px(7) - 2, h: 6 } },
  { kind: 'bc_guellSign', x: px(16), y: px(14) + 14 },
  { kind: 'bc_ironwork', x: px(5), y: px(18) + 12, w: 30, solid: { x: px(4) + 6, y: px(18) + 6, w: 20, h: 6 } },
  { kind: 'bc_lookout', x: px(4) + 8, y: px(2) + 14, w: 40, solid: { x: px(3) + 2, y: px(2) + 4, w: 44, h: 10 } },
  { kind: 'bc_parrots', x: px(9), y: px(5), w: 40 },
  { kind: 'bc_parrots', x: px(25), y: px(12), w: 40, variant: 1 },
  ...([[1, 3], [1, 7], [1, 12], [1, 16], [6, 12], [8, 15], [21, 12], [23, 3], [28, 3], [29, 8], [29, 13], [29, 17], [6, 1], [12, 1], [18, 1], [26, 1], [9, 13]] as const).map(([tx, ty], i) => (i % 3 === 2 ? tree(tx, ty, 'pine') : palm(tx, ty, i % 3, 8))),
  ...([[8, 4], [20, 5], [23, 6], [11, 6]] as const).map(([tx, ty], i): Decor => ({ kind: 'bc_agave', ...mid(tx, ty), variant: i % 2 })),
  crowd(16, 17, 5, 48, 20, 2, 3),
  crowd(12, 6, 4, 60, 24, 2, 4),
  crowd(20, 4, 3, 40, 20, 2, 5),
  person(13, 9, 2 * 4 + 1), person(18, 9, 9 * 4 + 1, 8, 12), person(26, 10, 14 * 4 + 1), person(4, 10, 21 * 4 + 1),
  { kind: 'bc_tripod', x: px(18) + 8, y: px(6) + 12 },
  pigeons(10, 7, 3), pigeons(20, 7, 4),
  { kind: 'bc_trashBin', ...mid(19, 19) },
  { kind: 'bc_leaves', x: px(4), y: px(14), w: 34, variant: 1 },
  { kind: 'bc_leaves', x: px(25), y: px(8), w: 34, variant: 2 },

  // ---------------------------------------------------------------------------------------------
  // The Eixample and the Sagrada Família
  // ---------------------------------------------------------------------------------------------
  facade(33, 1, 13, 7, 0), facade(33, 12, 13, 8, 1), facade(80, 1, 14, 7, 2), facade(80, 12, 14, 8, 3),
  { kind: 'bc_sagrada', ...span(54, 18), y: px(11), h: 144 },
  { kind: 'bc_crane', x: px(56) + 4, y: px(11), h: 120 },
  { kind: 'bc_crane', x: px(69) + 10, y: px(11), h: 104, variant: 1 },
  { kind: 'bc_pondRail', ...span(58, 10), y: px(17) + 2 },
  { kind: 'bc_pondRail', ...span(58, 10), y: px(15) + 1, variant: 1 },
  { kind: 'bc_reflection', ...span(58, 10), y: px(17), h: 32 },
  { kind: 'bc_queue', x: px(66), y: px(12) + 12, variant: 9 + 16 * 2 + 256 * 0 },
  crowd(57, 14, 6, 60, 22, 2, 1),
  crowd(70, 18, 4, 50, 20, 2, 6),
  crowd(53, 15, 3, 30, 20, 0, 7),
  person(56, 12, 4 * 4 + 1), person(61, 13, 18 * 4 + 1), person(64, 14, 7 * 4 + 1), person(73, 13, 25 * 4 + 1),
  { kind: 'bc_tripod', x: px(62) + 8, y: px(18) + 12, variant: 1 },
  { kind: 'bc_souvenir', x: px(52) + 8, y: px(13) + 14, w: 36, solid: { x: px(51) + 10, y: px(13) + 4, w: 28, h: 10 } },
  ...([[51, 12], [51, 17], [74, 12], [74, 17], [56, 19], [68, 19]] as const).map(([tx, ty], i) => (i % 2 ? plane(tx, ty, i % 3) : tree(tx, ty))),
  ...([[52, 0], [57, 0], [62, 0], [68, 0], [73, 0], [52, 5], [52, 9], [73, 5], [73, 9]] as const).map(([tx, ty], i): Decor => (i % 2 ? plane(tx, ty, i % 3) : tree(tx, ty))),
  { kind: 'bc_parterre', x: px(60), y: px(19) + 8, w: 56, h: 20, variant: 1, solid: { x: px(60) - 30, y: px(19) - 14, w: 60, h: 22 } },
  benchSit(57, 17, 1), benchSit(69, 17, 2),
  pigeons(63, 18, 1, 40),
  lamp(55, 13), lamp(70, 13), lamp(55, 19), lamp(70, 19),
  { kind: 'bc_kiosk', x: px(47) + 8, y: px(16) + 14, w: 30, variant: 1, solid: { x: px(47) - 4, y: px(16) + 6, w: 24, h: 8 } },
  // Eixample streets: taxis, a bus, scooters, lane arrows, traffic lights, Bicing, trees in grates.
  taxi(49, 3, 0), taxi(48, 14, 1), taxi(77, 6, 0), taxi(76, 16, 1),
  { kind: 'bc_bus', x: px(42), y: px(10) + 6, w: 90, variant: 0, solid: { x: px(40), y: px(9) + 8, w: 80, h: 12 } },
  { kind: 'bc_bus', x: px(86), y: px(10) + 6, w: 90, variant: 1, solid: { x: px(84), y: px(9) + 8, w: 80, h: 12 } },
  { kind: 'bc_laneArrow', x: px(48) + 8, y: px(6) + 14 }, { kind: 'bc_laneArrow', x: px(77) + 8, y: px(15) + 14 },
  { kind: 'bc_manhole', ...mid(49, 17) }, { kind: 'bc_manhole', ...mid(76, 4) },
  { kind: 'trafficLight', x: px(47) + 12, y: px(8) + 14 }, { kind: 'trafficLight', x: px(50) + 4, y: px(11) + 2 },
  { kind: 'trafficLight', x: px(78) + 4, y: px(8) + 14 }, { kind: 'trafficLight', x: px(75) + 12, y: px(11) + 2 },
  { kind: 'bc_vespa', ...mid(32, 8), variant: 0 }, { kind: 'bc_vespa', ...mid(32, 11), variant: 1 }, { kind: 'bc_vespa', ...mid(95, 8), variant: 2 }, { kind: 'bc_vespa', ...mid(95, 11), variant: 0 },
  bicing(34, 8, 5), bicing(84, 11, 5),
  ...([[36, 0], [42, 0], [84, 0], [90, 0], [36, 8], [42, 8], [84, 8], [90, 8], [36, 20], [42, 20], [84, 20], [90, 20], [47, 5], [47, 14], [78, 5], [78, 14]] as const).map(([tx, ty], i): Decor => ({ kind: 'bc_streetTree', ...mid(tx, ty), variant: i % 2, solid: { x: px(tx) + 2, y: px(ty) + 10, w: 12, h: 4 } })),
  crowd(40, 20, 4, 60, 10, 1, 2), crowd(88, 20, 4, 60, 10, 0, 8), crowd(44, 8, 3, 40, 10, 1, 9), crowd(90, 8, 3, 40, 10, 0, 10),
  { kind: 'bc_stroller', x: px(38) + 8, y: px(11) + 12, w: 1 },
  { kind: 'bc_senyera', x: px(36) + 8, y: px(8) }, { kind: 'bc_senyera', x: px(90) + 8, y: px(8), variant: 1 }, { kind: 'bc_senyera', x: px(41) + 8, y: px(20), variant: 1 }, { kind: 'bc_senyera', x: px(85) + 8, y: px(20) },
  { kind: 'bc_pharmacy', x: px(44) + 8, y: px(20) }, { kind: 'bc_pharmacy', x: px(82) + 8, y: px(8) },
  { kind: 'bc_awning', x: px(38) + 8, y: px(20), w: 40, variant: 0 }, { kind: 'bc_awning', x: px(92) + 8, y: px(20), w: 40, variant: 1 }, { kind: 'bc_awning', x: px(36) + 8, y: px(8), w: 40, variant: 2 },
  // The avenue: central trees, traffic, a bus stop, crossings with walkers.
  ...([4, 10, 20, 36, 42, 54, 68, 72, 90] as const).map((tx, i): Decor => ({ kind: 'bc_streetTree', x: px(tx) + 8, y: px(21) + 14, variant: i % 2, solid: { x: px(tx) + 2, y: px(21) + 10, w: 12, h: 4 } })),
  ...([8, 24, 40, 58, 66, 80, 94] as const).map((tx, i): Decor => ({ kind: 'bc_streetTree', x: px(tx) + 8, y: px(25) + 14, variant: (i + 1) % 2, solid: { x: px(tx) + 2, y: px(25) + 10, w: 12, h: 4 } })),
  taxi(6, 22, 0), taxi(38, 24, 1), taxi(56, 22, 0), taxi(70, 24, 1), taxi(90, 22, 0),
  { kind: 'bc_bus', x: px(20), y: px(24) + 6, w: 90, variant: 0, solid: { x: px(18), y: px(23) + 8, w: 80, h: 12 } },
  { kind: 'bc_bus', x: px(45), y: px(22) + 6, w: 90, variant: 1, solid: { x: px(43), y: px(21) + 8, w: 80, h: 12 } },
  { kind: 'bc_vespa', ...mid(12, 24), variant: 1 }, { kind: 'bc_vespa', ...mid(64, 24), variant: 2 }, { kind: 'bc_vespa', ...mid(80, 22), variant: 0 },
  { kind: 'bc_laneArrow', x: px(34) + 8, y: px(24) + 14, variant: 1 }, { kind: 'bc_laneArrow', x: px(74) + 8, y: px(22) + 14 }, { kind: 'bc_laneArrow', x: px(10) + 8, y: px(22) + 14 },
  { kind: 'bc_walkers', x: px(31), y: px(24) + 8, w: 40, variant: 16 + 1 }, { kind: 'bc_walkers', x: px(63), y: px(24) + 8, w: 40, variant: 16 + 2 }, { kind: 'bc_walkers', x: px(16), y: px(24) + 8, w: 40, variant: 16 + 3 },
  { kind: 'trafficLight', x: px(14) + 4, y: px(21) + 14 }, { kind: 'trafficLight', x: px(32) + 4, y: px(25) + 2 }, { kind: 'trafficLight', x: px(61) + 4, y: px(21) + 14 }, { kind: 'trafficLight', x: px(86) + 4, y: px(25) + 2 },
  { kind: 'bc_busStop', x: px(52) + 8, y: px(25) + 14, w: 40, solid: { x: px(51) + 4, y: px(25) + 8, w: 40, h: 6 } },
  { kind: 'bc_busStop', x: px(18) + 8, y: px(21) + 14, w: 40, variant: 1, solid: { x: px(17) + 4, y: px(21) + 8, w: 40, h: 6 } },
  falques(45, 25), falques(70, 21), falques(88, 25), falques(2, 25),
  crowd(52, 26, 4, 60, 10, 1, 11), crowd(22, 21, 3, 40, 8, 0, 12),
  { kind: 'bc_manhole', ...mid(20, 23) }, { kind: 'bc_manhole', ...mid(70, 22) },
  { kind: 'bc_puddle', x: px(44) + 8, y: px(22) + 12, w: 22 },

  // ---------------------------------------------------------------------------------------------
  // Plaça Catalunya and La Rambla
  // ---------------------------------------------------------------------------------------------
  { kind: 'bc_fountainCat', x: px(22) + 8, y: px(27) + 14, w: 40, solid: { x: px(21) + 8, y: px(27) - 2, w: 32, h: 14 } },
  pigeons(24, 28, 5, 50), pigeons(37, 27, 6, 40),
  crowd(32, 27, 6, 60, 14, 0, 13), crowd(40, 28, 3, 40, 10, 1, 14),
  { kind: 'bc_pigeonMan', x: px(37) + 8, y: px(28) + 12 },
  benchSit(20, 28, 3), benchSit(41, 28, 4),
  facade(16, 29, 8, 9, 1), facade(38, 29, 4, 4, 4), facade(38, 39, 4, 5, 5),
  { kind: 'bc_boqueria', ...span(18, 6), y: px(38), h: 60 },
  { kind: 'bc_liceu', ...span(38, 4), y: px(44), h: 70 },
  ...([30, 33, 36, 39] as const).map((ty, i): Decor => plane(28, ty, i % 3, 2)),
  ...([30, 33, 36, 39] as const).map((ty, i): Decor => plane(33, ty, (i + 1) % 3, 14)),
  { kind: 'bc_kiosk', x: px(31), y: px(40) + 14, w: 30, variant: 2, solid: { x: px(31) - 14, y: px(40) + 4, w: 28, h: 10 } },
  { kind: 'bc_painter', x: px(29) + 8, y: px(35) + 14, w: 30, solid: { x: px(29) + 4, y: px(35) + 8, w: 12, h: 6 } },
  { kind: 'bc_miro', x: px(31), y: px(38) + 16, w: 48 },
  crowd(30, 30, 5, 70, 12, 2, 15), crowd(31, 34, 5, 70, 12, 1, 0), crowd(30, 37, 4, 60, 10, 2, 1), crowd(31, 41, 4, 60, 10, 0, 2),
  crowd(25, 33, 3, 20, 40, 1, 3), crowd(36, 36, 3, 20, 40, 2, 4),
  { kind: 'bc_stroller', x: px(32) + 8, y: px(32) + 12, w: -1 },
  { kind: 'bc_cat', x: px(37) + 6, y: px(29) + 6, variant: 2 },
  ...([29, 35, 41] as const).map((ty): Decor => lamp(24, ty)),
  ...([32, 38] as const).map((ty): Decor => lamp(37, ty)),
  { kind: 'bc_trashBin', ...mid(25, 31) }, { kind: 'bc_trashBin', ...mid(36, 40) },
  { kind: 'bc_manhole', ...mid(27, 36) }, { kind: 'bc_manhole', ...mid(34, 31) },
  taxi(26, 31, 0), taxi(35, 36, 1), taxi(26, 39, 1),
  { kind: 'bc_vespa', ...mid(34, 40), variant: 2 },
  { kind: 'bc_leaves', x: px(30), y: px(33), w: 34, variant: 3 }, { kind: 'bc_leaves', x: px(32), y: px(40), w: 34, variant: 4 },
  { kind: 'bc_awning', x: px(21) + 8, y: px(38), w: 40, variant: 1 }, { kind: 'bc_awning', x: px(16) + 20, y: px(38), w: 36, variant: 2 },
  { kind: 'bc_senyera', x: px(17) + 8, y: px(38) }, { kind: 'bc_senyera', x: px(40) + 8, y: px(34), variant: 1 },
  // Drassanes and the Columbus roundabout.
  ...([[18, 38], [22, 42], [18, 43]] as const).map(([tx, ty], i) => palm(tx, ty, i % 3)),
  crowd(21, 39, 4, 50, 12, 3, 5), pigeons(29, 42, 7, 30),
  taxi(4, 40, 0), taxi(10, 41, 1), taxi(20, 41, 0),
  { kind: 'bc_laneArrow', x: px(14) + 8, y: px(40) + 14 },
  lamp(17, 39), lamp(25, 43), lamp(2, 39),

  // ---------------------------------------------------------------------------------------------
  // Barri Gòtic
  // ---------------------------------------------------------------------------------------------
  facade(42, 26, 8, 7, 4), facade(60, 26, 4, 7, 5), facade(66, 26, 7, 7, 4),
  { kind: 'bc_cathedral', ...span(50, 10), y: px(33), h: 112 },
  facade(42, 39, 8, 5, 5), facade(52, 39, 12, 5, 4), facade(64, 42, 9, 2, 5),
  { kind: 'bc_generalitat', ...span(64, 9), y: px(44), h: 32 },
  { kind: 'bc_bishopBridge', x: px(51), y: px(44), w: 32 },
  // Plaça Reial: palms, Gaudí's lamps, café terraces, the busker's corner.
  palm(40, 35, 0), palm(45, 34, 1), palm(40, 38, 2), palm(46, 38, 0),
  { kind: 'bc_gaudiLamp', x: px(43) + 8, y: px(36) + 14, solid: { x: px(43) + 4, y: px(36) + 8, w: 8, h: 6 } },
  terrace(39, 33, 0), terrace(44, 33, 1), terrace(47, 37, 2),
  { kind: 'bc_fountainReial', x: px(43) + 8, y: px(38) + 12, w: 30, solid: { x: px(43) - 4, y: px(38) - 2, w: 24, h: 12 } },
  // Pla de la Seu: the sardana ring, geese from the cloister, lamps, a tourist group.
  { kind: 'bc_sardana', x: px(55), y: px(36) + 8, w: 70 },
  { kind: 'bc_geese', x: px(61) + 8, y: px(34) + 12, w: 30 },
  lamp(49, 34), lamp(60, 34), lamp(49, 38), lamp(62, 38),
  crowd(52, 34, 4, 50, 10, 2, 6), crowd(60, 38, 3, 40, 8, 0, 7),
  { kind: 'bc_tripod', x: px(57) + 8, y: px(38) + 12, variant: 2 },
  { kind: 'bc_vsign', x: px(64) + 8, y: px(32) + 14, variant: 0 }, { kind: 'bc_vsign', x: px(49) + 8, y: px(42) + 14, variant: 2 }, { kind: 'bc_vsign', x: px(65) + 8, y: px(27) + 14, variant: 1 },
  { kind: 'bc_washing', x: px(64) + 8, y: px(29) + 14, w: 24 }, { kind: 'bc_washing', x: px(51), y: px(40) + 10, w: 24 },
  { kind: 'bc_cat', x: px(66) + 6, y: px(33) + 8, variant: 1 },
  // Plaça Sant Jaume: the castells and the crowd watching them.
  { kind: 'bc_castellCrowd', x: px(68) + 8, y: px(40) + 10, w: 90 },
  crowd(65, 37, 3, 30, 20, 5, 8), crowd(72, 38, 3, 20, 30, 5, 9),
  lamp(64, 36), lamp(72, 36), lamp(64, 41, 12),
  { kind: 'bc_senyera', x: px(66) + 8, y: px(44), variant: 2 }, { kind: 'bc_senyera', x: px(70) + 8, y: px(44), variant: 2 },
  { kind: 'bc_manhole', ...mid(58, 33) }, { kind: 'bc_puddle', x: px(65), y: px(30) + 12, w: 18 },
  { kind: 'bc_leaves', x: px(45), y: px(36), w: 30, variant: 5 },

  // ---------------------------------------------------------------------------------------------
  // Parc de la Ciutadella
  // ---------------------------------------------------------------------------------------------
  { kind: 'bc_arc', x: px(84), y: px(28) + 14, w: 96, solid: { x: px(82) + 4, y: px(28) + 8, w: 8, h: 6 } },
  { kind: 'bc_solid', x: px(85) + 8, y: px(28) + 14, solid: { x: px(85) + 4, y: px(28) + 8, w: 8, h: 6 } },
  { kind: 'bc_cascada', x: px(91), y: px(33) + 14, w: 80, solid: { x: px(88) + 4, y: px(31), w: 56, h: 30 } },
  { kind: 'bc_pondRail', ...span(74, 6), y: px(34) + 2 },
  { kind: 'bc_boats', x: px(77), y: px(32), w: 60 },
  { kind: 'bc_petancaCourt', ...span(74, 7), y: px(42) },
  { kind: 'bc_mammoth', x: px(79) + 8, y: px(27) + 14, solid: { x: px(78) + 4, y: px(27) + 4, w: 28, h: 10 } },
  ...([[75, 27], [80, 28], [87, 27], [94, 27], [75, 35], [81, 35], [88, 35], [75, 43], [80, 43], [89, 43], [94, 43], [88, 38], [94, 38], [79, 38]] as const).map(([tx, ty], i) => (i % 4 === 1 ? palm(tx, ty, i % 3) : i % 4 === 3 ? tree(tx, ty, 'pine') : tree(tx, ty))),
  { kind: 'bc_parterre', x: px(91), y: px(29) + 8, w: 48, h: 20, variant: 3, solid: { x: px(91) - 26, y: px(29) - 14, w: 52, h: 22 } },
  benchSit(80, 36, 5), benchSit(88, 36, 6), benchSit(83, 43, 7),
  { kind: 'bc_parrots', x: px(78), y: px(27), w: 40, variant: 2 }, { kind: 'bc_parrots', x: px(92), y: px(42), w: 40 },
  crowd(84, 32, 4, 40, 30, 0, 10), crowd(84, 40, 3, 40, 30, 1, 11),
  { kind: 'bc_picnic', x: px(78), y: px(34) + 14, w: 40 }, { kind: 'bc_picnic', x: px(91), y: px(40) + 14, w: 40, variant: 1 },
  { kind: 'bc_stroller', x: px(84), y: px(37) + 12, w: 1, variant: 2 },
  lamp(82, 30), lamp(86, 30), lamp(82, 42), lamp(86, 42),
  { kind: 'bc_leaves', x: px(77), y: px(37), w: 34, variant: 6 }, { kind: 'bc_leaves', x: px(90), y: px(37), w: 34, variant: 7 },
  { kind: 'bc_cat', x: px(76) + 8, y: px(42) + 8, variant: 0 },

  // ---------------------------------------------------------------------------------------------
  // Port Vell and the Barceloneta
  // ---------------------------------------------------------------------------------------------
  { kind: 'bc_quay', ...span(16, 26), y: px(46) + 2 },
  { kind: 'bc_yacht', x: px(20), y: px(50), w: 60 }, { kind: 'bc_yacht', x: px(37), y: px(49), w: 60, variant: 1 }, { kind: 'bc_yacht', x: px(24), y: px(54), w: 60, variant: 2 },
  { kind: 'bc_golondrina', x: px(36), y: px(54), w: 60 },
  { kind: 'bc_deckRail', ...span(28, 6), y: px(53) },
  { kind: 'bc_deckRail', ...span(30, 2), y: px(46), variant: 1 },
  crowd(31, 52, 3, 50, 10, 2, 12),
  { kind: 'bc_gulls', x: px(26), y: px(48), w: 60 }, { kind: 'bc_gulls', x: px(70), y: px(53), w: 60, variant: 1 },
  ...([18, 24, 36, 44, 52, 62, 70, 78, 86] as const).map((tx, i) => palm(tx, 45, i % 3, 8)),
  ...([22, 40, 48, 58, 66, 74, 82] as const).map((tx): Decor => lamp(tx, 44, 8)),
  benchSit(28, 45, 8), benchSit(56, 45, 9), benchSit(76, 45, 10),
  { kind: 'bc_skaters', x: px(60), y: px(44) + 8, w: 160 }, { kind: 'bc_skaters', x: px(30), y: px(45) + 8, w: 160, variant: 1 },
  bicing(48, 45, 4),
  { kind: 'bc_promRail', ...span(42, 48), y: px(46) + 1 },
  { kind: 'bc_shower', ...mid(50, 46) }, { kind: 'bc_shower', ...mid(72, 46) },
  { kind: 'lifeguardChair', x: px(68) + 8, y: px(49) + 10 },
  { kind: 'volleyNet', x: px(79) + 8, y: px(48) + 8, w: 48 },
  { kind: 'bc_sunbathers', x: px(46), y: px(49) + 8, w: 60 }, { kind: 'bc_sunbathers', x: px(55), y: px(50) + 8, w: 60, variant: 1 }, { kind: 'bc_sunbathers', x: px(75), y: px(49) + 10, w: 60, variant: 2 }, { kind: 'bc_sunbathers', x: px(86), y: px(48) + 10, w: 60, variant: 3 },
  { kind: 'bc_sandcastle', ...mid(64, 49) },
  { kind: 'bc_surf', ...span(42, 54), y: px(51) + 2 },
  { kind: 'bc_wHotel', ...span(90, 6), y: px(50), h: 96 },
  { kind: 'bc_parrots', x: px(44), y: px(44), w: 40, variant: 1 },
  { kind: 'bc_cat', x: px(94) + 8, y: px(50) + 12, variant: 3 },
  { kind: 'bc_trashBin', ...mid(53, 44) }, { kind: 'bc_trashBin', ...mid(84, 44) },
  crowd(46, 47, 4, 40, 10, 2, 13), crowd(88, 46, 3, 30, 10, 1, 14), crowd(66, 47, 4, 50, 10, 0, 15),
  { kind: 'bc_gulls', x: px(55), y: px(53), w: 60, variant: 2 },

  // ---------------------------------------------------------------------------------------------
  // El Prat arrivals
  // ---------------------------------------------------------------------------------------------
  { kind: 'bc_terminal', ...span(0, 16), y: px(50), h: 96 },
  { kind: 'bc_arrivalsSign', x: px(8), y: px(42) + 2, w: 90 },
  { kind: 'bc_aerobus', x: px(12), y: px(44) + 10, w: 70, solid: { x: px(10) - 6, y: px(43) + 10, w: 66, h: 12 } },
  taxi(2, 43, 0), taxi(4, 44, 1), taxi(6, 43, 0),
  { kind: 'bc_taxiSign', x: px(1) + 8, y: px(42) + 14 },
  { kind: 'bc_l9sign', x: px(14) + 8, y: px(47) + 14, solid: { x: px(14) + 4, y: px(47) + 8, w: 8, h: 6 } },
  crowd(5, 47, 5, 50, 16, 3, 1), crowd(11, 48, 3, 40, 10, 3, 2),
  { kind: 'bc_queue', x: px(12) + 4, y: px(46) + 10, variant: 4 + 16 * 3 + 256 * 1 },
  palm(1, 46, 1), palm(8, 42, 2), palm(15, 42, 0),
  { kind: 'bc_trolleys', x: px(9) + 8, y: px(49) + 12, w: 30 },
  { kind: 'bc_jet', x: px(8), y: px(38), w: 180 },
  { kind: 'bc_flags', x: px(3), y: px(49) + 14 },
  { kind: 'bc_benchAirport', x: px(3) + 8, y: px(46) + 14, w: 30, solid: { x: px(3) - 6, y: px(46) + 8, w: 28, h: 6 } },
  lamp(0, 42, 12), lamp(9, 38), lamp(13, 38),
  { kind: 'bc_manhole', ...mid(3, 48) },
];

// Terrace parapet of Park Güell continues down both sides of the stairway.
decor.push({ kind: 'bc_terraceEdge', ...span(10, 4), y: px(11) + 8, variant: 1 }, { kind: 'bc_terraceEdge', ...span(18, 4), y: px(11) + 8, variant: 1 });

const lights: Light[] = [
  // Lamps (warm) along the Rambla, the squares, the promenade and the avenue.
  ...([[24, 29], [24, 35], [24, 41], [37, 32], [37, 38], [49, 34], [60, 34], [49, 38], [62, 38], [64, 36], [72, 36], [55, 13], [70, 13], [55, 19], [70, 19], [17, 39], [25, 43], [2, 39], [82, 30], [86, 30], [82, 42], [86, 42], [0, 42], [9, 38], [13, 38]] as const)
    .map(([tx, ty]) => ({ x: px(tx) + 8, y: px(ty) + 2, r: 46, color: '255,220,150' })),
  ...([22, 40, 48, 58, 66, 74, 82] as const).map((tx) => ({ x: px(tx) + 8, y: px(44) + 2, r: 50, color: '255,220,150' })),
  ...([[45, 25], [70, 21], [88, 25], [2, 25]] as const).map(([tx, ty]) => ({ x: px(tx) + 8, y: px(ty) - 2, r: 44, color: '255,214,140' })),
  { x: px(43) + 8, y: px(36), r: 60, color: '255,200,120' }, // Gaudí's lamp in Plaça Reial
  // Shop fronts, kiosks, the granja, the Boqueria entrance, the Liceu.
  { x: px(31), y: px(31) + 2, r: 40, color: '255,180,200' },
  { x: px(31), y: px(40) + 2, r: 40, color: '255,180,200' },
  { x: px(21), y: px(37), r: 70, color: '255,200,120' },
  { x: px(40), y: px(43), r: 60, color: '255,220,170' },
  { x: px(67), y: px(35), r: 44, color: '255,190,120' },
  { x: px(52) + 8, y: px(15), r: 40, color: '255,200,140' },
  { x: px(47) + 8, y: px(16), r: 36, color: '255,200,140' },
  // The Sagrada Família and the cathedral lit at night; the Arc; the Cascada; Columbus; the W.
  { x: px(63), y: px(8), r: 150, color: '255,200,130' },
  { x: px(55), y: px(30), r: 90, color: '255,210,150' },
  { x: px(84), y: px(27), r: 60, color: '255,190,120' },
  { x: px(91), y: px(32), r: 60, color: '200,220,255' },
  { x: px(31), y: px(40), r: 60, color: '255,210,150' },
  { x: px(93), y: px(46), r: 60, color: '160,200,255' },
  // Chiringuito, the terminal glass, the Aerobús, metro signs.
  { x: px(61), y: px(46), r: 60, color: '255,200,130' },
  { x: px(8), y: px(49), r: 120, color: '200,230,255' },
  { x: px(12), y: px(44), r: 40, color: '150,200,255' },
  ...([[27, 19], [52, 19], [36, 37], [43, 44]] as const).map(([tx, ty]) => ({ x: px(tx) + 8, y: px(ty) + 2, r: 30, color: '255,80,80' })),
  // Terraces and the port.
  { x: px(41), y: px(33), r: 44, color: '255,200,140' },
  { x: px(48), y: px(37), r: 44, color: '255,200,140' },
  { x: px(31), y: px(51), r: 50, color: '255,220,170' },
];

export const barcelona: MapDef = {
  id: 'barcelona',
  w: W,
  h: H,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e8dcc4', wallLight: '#f6eedc', wainscot: '#8a6a4a', wallTop: '#3a2a20' },
  doors: [],
  decor,
  areas: [
    { key: 'bc_airport', rect: { x: 0, y: px(38), w: px(16), h: px(18) } },
    { key: 'bc_guell', rect: { x: 0, y: 0, w: px(32), h: px(21) } },
    { key: 'bc_sagrada', rect: { x: px(32), y: 0, w: px(64), h: px(26) } },
    { key: 'bc_rambla', rect: { x: px(16), y: px(26), w: px(26), h: px(18) } },
    { key: 'bc_gotic', rect: { x: px(42), y: px(26), w: px(31), h: px(18) } },
    { key: 'bc_park', rect: { x: px(73), y: px(26), w: px(23), h: px(18) } },
    { key: 'bc_beach', rect: { x: px(16), y: px(44), w: px(80), h: px(12) } },
    { key: 'bc_city', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: {
    /** Out of the arrivals hall, in front of the Aerobús stop. */
    airport: { x: px(7) + 8, y: px(47) + 8, facing: 'up' },
    catalunya: { x: px(30) + 8, y: px(27) + 8, facing: 'down' },
    guell: { x: px(26) + 8, y: px(19) + 8, facing: 'up' },
    sagrada: { x: px(53) + 8, y: px(18) + 8, facing: 'up' },
    liceu: { x: px(36) + 8, y: px(35) + 8, facing: 'left' },
    beach: { x: px(44) + 8, y: px(45) + 4, facing: 'down' },
  },
};
